/**
 * Email Config List Component
 *
 * Displays list of email configurations with actions
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ActivateIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { EmailConfig } from '../../types/emailProcessor';
import { primaryButtonSx } from '../../theme';
import emailProcessorService from '../../services/emailProcessorService';
import EmailConfigForm from './EmailConfigForm';

interface EmailConfigListProps {
  onError: (error: string) => void;
}

const EmailConfigList: React.FC<EmailConfigListProps> = ({ onError }) => {
  const [configs, setConfigs] = useState<EmailConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<EmailConfig | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<EmailConfig | null>(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const data = await emailProcessorService.getEmailConfigs();
      setConfigs(data);
    } catch (error: any) {
      onError(error.response?.data?.detail || 'Failed to load email configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedConfig(null);
    setFormOpen(true);
  };

  const handleEdit = (config: EmailConfig) => {
    setSelectedConfig(config);
    setFormOpen(true);
  };

  const handleDelete = (config: EmailConfig) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!configToDelete) return;

    try {
      await emailProcessorService.deleteEmailConfig(configToDelete.id);
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
      loadConfigs();
    } catch (error: any) {
      onError(error.response?.data?.detail || 'Failed to delete configuration');
    }
  };

  const handleActivate = async (config: EmailConfig) => {
    try {
      await emailProcessorService.activateEmailConfig(config.id);
      loadConfigs();
    } catch (error: any) {
      onError(error.response?.data?.detail || 'Failed to activate configuration');
    }
  };

  const handleDeactivate = async (config: EmailConfig) => {
    try {
      await emailProcessorService.deactivateEmailConfig(config.id);
      loadConfigs();
    } catch (error: any) {
      onError(error.response?.data?.detail || 'Failed to deactivate configuration');
    }
  };

  const handleCheckNow = async (config: EmailConfig) => {
    try {
      const result = await emailProcessorService.checkEmailsNow(config.id);
      alert(`Found ${result.new_threads} new emails. Auto-processed: ${result.auto_processed}`);
      loadConfigs();
    } catch (error: any) {
      onError(error.response?.data?.detail || 'Failed to check emails');
    }
  };

  const handleFormClose = (success: boolean) => {
    setFormOpen(false);
    setSelectedConfig(null);
    if (success) {
      loadConfigs();
    }
  };

  if (loading && configs.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Email Configurations</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={primaryButtonSx}>
          Add Configuration
        </Button>
      </Box>

      {/* Config List */}
      {configs.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No email configurations yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add an email account to start monitoring attachments
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={primaryButtonSx}>
                Add Your First Configuration
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {configs.map((config) => (
            <Grid item xs={12} md={6} lg={4} key={config.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Typography variant="h6" noWrap sx={{ flex: 1 }}>
                      {config.email_address}
                    </Typography>
                    <Chip
                      label={config.status}
                      color={emailProcessorService.getConfigStatusColor(config.status) as any}
                      size="small"
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Server: {config.imap_server}:{config.imap_port}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Check Interval: {config.check_interval_minutes} minutes
                  </Typography>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Auto-process: {config.auto_process ? 'Yes' : 'No'}
                  </Typography>

                  {config.last_check_at && (
                    <Typography variant="caption" color="text.secondary">
                      Last checked: {format(new Date(config.last_check_at), 'PPp')}
                    </Typography>
                  )}

                  {config.last_error && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                      Error: {config.last_error}
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(config)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(config)}
                    >
                      Delete
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {config.status === 'active' && (
                      <>
                        <Tooltip title="Check Now">
                          <IconButton size="small" onClick={() => handleCheckNow(config)}>
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deactivate">
                          <IconButton size="small" onClick={() => handleDeactivate(config)} color="warning">
                            <StopIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {config.status === 'inactive' && (
                      <Tooltip title="Activate">
                        <IconButton size="small" onClick={() => handleActivate(config)} color="success">
                          <ActivateIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Form Dialog */}
      <EmailConfigForm
        open={formOpen}
        config={selectedConfig}
        onClose={handleFormClose}
        onError={onError}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this email configuration? All associated threads and attachments will be deleted.
          </Typography>
          {configToDelete && (
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
              {configToDelete.email_address}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailConfigList;
